import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { Field, sel, todayIso, useAccounts, AccLabel, accColor } from "@/lib/finance";
import { useAuth } from "@/context/AuthContext";
import InstrumentPicker from "@/components/prim/InstrumentPicker";
import { openHandover } from "@/components/prim/handover";

// v1.40.0 Personel Avans Ver: hak edişten bağımsız; kasa/banka, portföy evrakı (TL) veya karma. Prim Avansı bakiyesine eklenir, sonraki hak edişten mahsup edilir.
export default function AdvanceDialog({ staff, onClose, onDone }) {
  const { can, user } = useAuth();
  const accounts = useAccounts().filter((a) => a.type === "kasa" || a.type === "banka");
  const canDocs = user?.is_super || can("cek_senet", "edit");
  const [f, setF] = useState({ amount: "", account_id: "", date: todayIso(), reason: "" });
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const cash = Number(f.amount || 0);
  const docTotal = Object.values(docs).reduce((s, i) => s + i.amount, 0);
  const acc = f.account_id || accounts[0]?.id || "";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prim/advances", { user_id: staff.user_id, amount: cash, account_id: cash > 0 ? acc : null, instrument_ids: Object.keys(docs), date: f.date, reason: f.reason });
      toast.success(`Avans ${data.payment_no} verildi (${formatTRY(data.amount)})`, data.instruments.length ? { action: { label: "Teslim Tutanağı", onClick: () => openHandover(data.id) }, duration: 10000 } : {});
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="prim-advance-dialog">
        <DialogHeader><DialogTitle>Avans Ver · {staff.name}</DialogTitle><DialogDescription className="sr-only">Personele avans ödemesi</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2 text-[12px] text-slate-600">Avans "Prim Avansı" bakiyesine eklenir (mevcut: <b className="font-mono">{formatTRY(staff.advance_balance || 0)}</b>) ve sonraki hak edişlerden otomatik mahsup edilir.</div>
          {canDocs && <InstrumentPicker picked={docs} onChange={setDocs} />}
          <Field label="Kasa/Banka tutarı (TL)"><Input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} data-testid="advance-amount" /></Field>
          <Field label={<AccLabel text="Kasa / Banka" color={accColor(accounts, acc)} />}>
            <select className={`${sel} w-full`} value={acc} onChange={set("account_id")} disabled={!cash} data-testid="advance-account">{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
          <Field label="Tarih"><Input type="date" value={f.date} max={todayIso()} onChange={set("date")} data-testid="advance-date" /></Field>
          <Field label="Gerekçe / açıklama (zorunlu)"><Input value={f.reason} onChange={set("reason")} data-testid="advance-reason" /></Field>
          <div className="col-span-2 text-[12px]" data-testid="advance-breakdown">Evrak <b className="font-mono">{formatTRY(docTotal)}</b> + kasa/banka <b className="font-mono">{formatTRY(cash)}</b> = avans <b className="font-mono">{formatTRY(docTotal + cash)}</b></div>
        </div>
        <DialogFooter><Button disabled={busy || f.reason.trim().length < 5 || docTotal + cash <= 0} onClick={submit} data-testid="advance-submit">Avans Ver</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
